import React, { PureComponent } from 'react';
import { Checkbox, Col, Input, InputNumber, Row, Select } from 'antd';
import { MeasureType, RegulationDto, RegulationReasonType } from 'app/model/CORE';
import { InputDuration } from 'app/common/input/InputDuration';
import TimeUtils from '../../../common/util/TimeUtils';
import InputTime from '../../../common/input/InputTime';
import { Flex } from '../../common/Flex';
import MeasureUtils from '../../../common/util/MeasureUtils';
import Utils from '../../../common/util/Utils';
import AppContext from '../../../common/context/AppContext';
import { InitialConstraintsEditor } from './InitialConstraintsEditor';
import ConditionalRender from 'app/common/components/ConditionalRender';
import TextArea from 'antd/es/input/TextArea';
import { OccupancyConstraintsEditor } from 'app/components/panels/measure/OccupancyConstraintsEditor';

interface MiniRegulationEditorPanelProps {
    regulation: RegulationDto;
    onRegulationUpdated?: (RegulationDto) => void;
    readonly?: boolean;
}

interface MiniRegulationEditorPanelState {
    regulation: RegulationDto;
}

export class MiniRegulationEditorPanel extends PureComponent<
    MiniRegulationEditorPanelProps,
    MiniRegulationEditorPanelState
> {
    constructor(props) {
        super(props);
        this.state = {
            regulation: props.regulation,
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props?.regulation !== prevProps?.regulation) {
            this.setState({ regulation: this.props?.regulation });
        }
    }

    updateRegulation = <K extends keyof MiniRegulationEditorPanelState>(
        update: Partial<RegulationDto>,
        stateUpdate?: Partial<MiniRegulationEditorPanelState>,
    ) => {
        let updatedRegulation = Object.assign({}, this.state.regulation, update);
        updatedRegulation = MeasureUtils.adaptConstraintPeriods(
            updatedRegulation,
            Utils.isNotEmpty(update.initialConstraints),
            Utils.isNotEmpty(update.occupancyConstraints),
        );
        const updatedState: Pick<MiniRegulationEditorPanelState, K> = Object.assign(
            {},
            { regulation: updatedRegulation },
            stateUpdate as Pick<MiniRegulationEditorPanelState, K>,
        );
        this.setState(updatedState, () => this.props.onRegulationUpdated?.(updatedRegulation));
    };

    updateRate = (rate: number) => {
        const pendingRate = MeasureUtils.getDefaultPendingRate(rate);
        const normalRate = rate - pendingRate;

        const newConstraints = this.state.regulation.initialConstraints.map(constraint => ({
            ...constraint,
            normalRate,
            pendingRate,
        }));
        this.updateRegulation({ initialConstraints: newConstraints });
    };

    updatePeak = (peak: number) => {
        const newConstraints = this.state.regulation.occupancyConstraints.map(constraint => ({
            ...constraint,
            peakCapacity: peak,
        }));
        this.updateRegulation({ occupancyConstraints: newConstraints });
    };

    updateSustain = (sustain: number) => {
        const newConstraints = this.state.regulation.occupancyConstraints.map(constraint => ({
            ...constraint,
            occupancyRate: sustain,
        }));
        this.updateRegulation({ occupancyConstraints: newConstraints });
    };

    updateStartTime = (startTime: Date) => {
        let from = null;

        if (startTime) {
            // Start time date has to be TODAY
            from = TimeUtils.mergeDateAndTime(AppContext.getMonitoredDay(), startTime);
        }
        return this.updateRegulation({
            applicability: {
                from: from,
                to: this.state.regulation?.applicability?.to,
            },
        });
    };

    updateEndTime = (endTime: Date) => {
        let to = null;
        if (endTime) {
            // End time date will either be the day of the start time, or the next day, or TODAY()
            let day =
                (this.state.regulation?.applicability?.from &&
                    TimeUtils.withoutTimeOfDay(this.state.regulation?.applicability?.from)) ||
                AppContext.getMonitoredDay();
            if (
                this.state.regulation?.applicability?.from &&
                TimeUtils.timeSinceMidnightInMs(endTime) <=
                    TimeUtils.timeSinceMidnightInMs(this.state.regulation?.applicability?.from)
            ) {
                day = TimeUtils.addDays(day, 1);
            }
            to = TimeUtils.addMillis(day, TimeUtils.timeSinceMidnightInMs(endTime));
        }
        return this.updateRegulation({
            applicability: {
                from: this.state.regulation?.applicability?.from,
                to: to,
            },
        });
    };

    getDuration = (): number => {
        if (this.state.regulation?.applicability?.from && this.state.regulation?.applicability?.to) {
            return TimeUtils.diffInMs(
                this.state.regulation?.applicability?.to,
                this.state.regulation?.applicability?.from,
            );
        }
    };

    updateDuration = (duration: number) => {
        if (this.state.regulation?.applicability?.from) {
            this.updateEndTime(TimeUtils.addMillis(this.state.regulation?.applicability?.from, duration));
        } else if (this.state.regulation?.applicability?.to) {
            this.updateStartTime(TimeUtils.addMillis(this.state.regulation?.applicability?.to, -1 * duration));
        }
    };

    updateIdentifier = (id: string) => {
        this.updateRegulation({
            measureId: {
                id: id,
                type: this.state.regulation?.measureId?.type || MeasureType.REGULATION,
            },
        });
    };

    render() {
        const maxEntryRate =
            Utils.size(this.state.regulation.initialConstraints) > 0
                ? Math.max(...this.state.regulation.initialConstraints.map(MeasureUtils.getRate))
                : 0;
        const maxPeakRate =
            Utils.size(this.state.regulation.occupancyConstraints) > 0
                ? Math.max(...this.state.regulation.occupancyConstraints?.map(constraint => constraint.peakCapacity))
                : 0;
        const maxOccRate =
            Utils.size(this.state.regulation.occupancyConstraints) > 0
                ? Math.max(...this.state.regulation.occupancyConstraints?.map(MeasureUtils.getOccupancyRate))
                : 0;
        return (
            <Flex direction="vertical" style={{ fontWeight: 'bold' }}>
                <Row gutter={[8, 8]}>
                    <Col span={3} className={`${this.props.readonly ? 'disabled' : ''}`}>
                        Identifier
                        {/* MeasureId is a key used in the SolverPanel, so can't edit it for now */}
                        <Input
                            disabled={true}
                            value={this.state.regulation?.measureId?.id}
                            onChange={value => this.updateIdentifier(value.currentTarget.value)}
                        />
                    </Col>
                    <Col span={5} className={`${this.props.readonly ? 'disabled' : ''}`}>
                        Reason
                        <Select
                            value={this.state.regulation?.reason}
                            disabled={true}
                            listHeight={320}
                            style={{ width: '100%' }}>
                            <Select.Option value={RegulationReasonType.ACCIDENT_INCIDENT}>
                                Accident/Incident
                            </Select.Option>
                            <Select.Option value={RegulationReasonType.AERODROME_CAPACITY}>
                                Aerodrome Capacity
                            </Select.Option>
                            <Select.Option value={RegulationReasonType.ATC_INDUSTRIAL_ACTION}>
                                ATC Ind Action
                            </Select.Option>
                            <Select.Option value={RegulationReasonType.AIRSPACE_MANAGEMENT}>
                                Airspace Management
                            </Select.Option>
                            <Select.Option value={RegulationReasonType.OTHERS}>Other</Select.Option>
                            <Select.Option value={RegulationReasonType.SPECIAL_EVENT}>Special Event</Select.Option>
                            <Select.Option value={RegulationReasonType.ATC_ROUTINGS}>ATC Routings</Select.Option>
                            <Select.Option value={RegulationReasonType.ATC_STAFFING}>ATC Staffing</Select.Option>
                            <Select.Option value={RegulationReasonType.ATC_EQUIPMENT}>ATC Equipment</Select.Option>
                            <Select.Option value={RegulationReasonType.ENVIRONMENTAL_ISSUES}>
                                Environmental Issues
                            </Select.Option>
                            <Select.Option value={RegulationReasonType.WEATHER}>Weather</Select.Option>
                            <Select.Option value={RegulationReasonType.AERODROME_SERVICES}>
                                Aerodrome Services
                            </Select.Option>
                            <Select.Option value={RegulationReasonType.NON_ATC_INDUSTRIAL_ACTION}>
                                Ind Action non-ATC
                            </Select.Option>
                            <Select.Option value={RegulationReasonType.ATC_CAPACITY}>ATC Capacity</Select.Option>
                        </Select>
                    </Col>
                    <ConditionalRender condition={MeasureUtils.isEntryRegulation(this.state.regulation)}>
                        <Col span={2} className={`${this.props.readonly ? 'disabled' : ''}`}>
                            Rate
                            <InputNumber
                                min={0}
                                value={maxEntryRate}
                                onChange={value => this.updateRate(value)}
                                style={{ width: '4em' }}
                            />
                        </Col>
                    </ConditionalRender>
                    <ConditionalRender condition={MeasureUtils.isOccupancyRegulation(this.state.regulation)}>
                        <Col span={2} className={`${this.props.readonly ? 'disabled' : ''}`}>
                            Peak
                            <InputNumber
                                min={0}
                                value={maxPeakRate}
                                onChange={value => this.updatePeak(value)}
                                style={{ width: '4em' }}
                            />
                        </Col>
                        <Col span={2} className={`${this.props.readonly ? 'disabled' : ''}`}>
                            Sustain
                            <InputNumber
                                min={0}
                                value={maxOccRate}
                                onChange={value => this.updateSustain(value)}
                                style={{ width: '4em' }}
                            />
                        </Col>
                    </ConditionalRender>
                    <Col span={2} className={`${this.props.readonly ? 'disabled' : ''}`}>
                        Start time
                        <InputTime
                            value={this.state.regulation?.applicability?.from}
                            onChange={value => this.updateStartTime(value)}
                        />
                    </Col>
                    <Col span={2} className={`${this.props.readonly ? 'disabled' : ''}`}>
                        End time
                        <InputTime
                            value={this.state.regulation?.applicability?.to}
                            onChange={value => this.updateEndTime(value)}
                        />
                    </Col>
                    <Col span={2} className={`${this.props.readonly ? 'disabled' : ''}`}>
                        Duration
                        <InputDuration value={this.getDuration()} onChange={value => this.updateDuration(value)} />
                    </Col>
                    <ConditionalRender condition={MeasureUtils.isEntryRegulation(this.state.regulation)}>
                        <Col span={2}>
                            W. width
                            <InputDuration
                                value={this.state.regulation?.noDelayWindowInMs}
                                onChange={value => this.updateRegulation({ noDelayWindowInMs: value })}
                            />
                        </Col>
                    </ConditionalRender>
                    <Col span={3}>
                        Delay threshold
                        <InputDuration
                            value={this.state.regulation?.delayConfirmationThresholdInMs}
                            onChange={value => this.updateRegulation({ delayConfirmationThresholdInMs: value })}
                        />
                    </Col>
                    <Col span={2}>
                        <Checkbox
                            checked={this.state.regulation?.autoLink}
                            onChange={e => this.updateRegulation({ autoLink: e.target.checked })}>
                            Auto Link
                        </Checkbox>
                    </Col>
                </Row>
                <Row gutter={[8, 8]} style={{ paddingTop: '1em' }}>
                    <ConditionalRender condition={MeasureUtils.isEntryRegulation(this.state.regulation)}>
                        <Col span={14}>
                            <InitialConstraintsEditor
                                regulation={this.state.regulation}
                                onInitialConstraintsUpdate={this.updateRegulation}
                                readonly={this.props.readonly}
                            />
                        </Col>
                    </ConditionalRender>
                    <ConditionalRender condition={MeasureUtils.isOccupancyRegulation(this.state.regulation)}>
                        <Col span={14}>
                            <OccupancyConstraintsEditor
                                regulation={this.state.regulation}
                                onOccupancyConstraintsUpdate={this.updateRegulation}
                                readonly={this.props.readonly}
                            />
                        </Col>
                    </ConditionalRender>
                    <Col span={9}>
                        ANM remark
                        <TextArea
                            value={this.state.regulation?.remark}
                            onChange={value => this.updateRegulation({ remark: value.currentTarget.value })}
                            rows={4}
                        />
                    </Col>
                </Row>
            </Flex>
        );
    }
}
